import {
  type DynamicClient,
  InvalidParamError,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import { assertDefined } from '@dynamic-labs-sdk/client/core';
import type { TransferAmountParams } from '@dynamic-labs-sdk/client/core';
import { encodeFunctionData, erc20Abi, parseEther, parseUnits } from 'viem';

import { createPublicClientFromNetworkData } from '../../viem/createPublicClientFromNetworkData';
import { createWalletClientForWalletAccount } from '../../viem/createWalletClientForWalletAccount';
import { isEvmWalletAccount } from '../isEvmWalletAccount';

const DEFAULT_DECIMALS = 18;

export const evmTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isEvmWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account is not an EVM wallet account');
  }

  const { networkData } = await getActiveNetworkData({ walletAccount }, client);

  assertDefined(
    networkData,
    `No network data found for wallet account ${walletAccount.address}`
  );

  const publicClient = createPublicClientFromNetworkData({ networkData });
  const nonce = await publicClient.getTransactionCount({
    address: walletAccount.address as `0x${string}`,
  });

  const walletClient = await createWalletClientForWalletAccount(
    { walletAccount },
    client
  );

  // non-native token transfers
  if (token) {
    const decimals = token.decimals ?? DEFAULT_DECIMALS;

    const data = encodeFunctionData({
      abi: erc20Abi,
      args: [recipient as `0x${string}`, parseUnits(amount, decimals)],
      functionName: 'transfer',
    });

    const hash = await walletClient.sendTransaction({
      data,
      nonce,
      to: token.address as `0x${string}`,
    });

    return { transactionHash: hash };
  }

  // native token transfers
  const hash = await walletClient.sendTransaction({
    nonce,
    to: recipient as `0x${string}`,
    value: parseEther(amount),
  });

  return { transactionHash: hash };
};
